import React from "react"
import { Info } from "lucide-react"
import { Label } from "../../../ui/label"
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";
import { cn } from "../../../lib/utils"

/**
 * SelectDropdown - Migrated to use shadcn/ui Select component
 * 
 * @param {string} title - Select label
 * @param {string} name - Select name
 * @param {string} value - Selected value
 * @param {function} setValue - Change handler (receives event-like object)
 * @param {Array} options - Options array: [{ id, value, label }]
 * @param {string} errorMsg - Error message
 * @param {function} onSubmit - Submit handler
 * @param {function} setCouponCode - Special handler for bonusType
 * @param {string} ids - Select ID
 * @param {function} onFocus - Focus handler
 * @param {string} info - Tooltip text
 * @param {boolean} noSelectOpt - Hide default "Select" option
 * @param {boolean} disabled - Disabled state
 * @param {boolean} lengthyTitle - Allow longer labels (60 chars vs 25)
 * @param {boolean} sportsFreez - Show "All Sports" instead of "Select"
 * @param {string} size - Trigger height: "default" (h-10) or "sm" (h-9)
 */
const SelectDropdown = (props) => {
  const {
    title,
    name,
    value,
    setValue,
    options,
    errorMsg,
    onSubmit,
    setCouponCode,
    ids,
    onFocus,
    info,
    noSelectOpt,
    disabled,
    lengthyTitle,
    sportsFreez,
    size = "default",
    // Deliberately swallowed so stray props never reach the DOM node.
    // eslint-disable-next-line no-unused-vars
    ...rest
  } = props

  // Compute display value directly from prop — fully controlled, no internal state lag
  const displayValue = (value === "" || value == null) ? "__SELECT_PLACEHOLDER__" : value

  const handleChange = (newValue) => {
    // Convert placeholder back to empty string for backward compatibility
    const actualValue = newValue === "__SELECT_PLACEHOLDER__" ? "" : newValue

    // Create event-like object for compatibility
    const eventLike = {
      target: {
        name,
        value: actualValue,
      },
    }

    setValue(eventLike)

    if (name === "bonusType" && setCouponCode) {
      setCouponCode()
    }
  }

  const truncateLabel = (label) => {
    const maxLength = lengthyTitle ? 60 : 25
    return label?.length > maxLength ? `${label.substring(0, maxLength)}...` : label
  }

  return (
    <div className="space-y-2">
      {title && (
        <Label htmlFor={ids} className="flex items-center gap-2">
          {title}
          {info && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{info}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Label>
      )}

      <ShadcnSelect
        value={displayValue}
        onValueChange={handleChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={ids}
          onFocus={onFocus}
          aria-invalid={errorMsg ? true : undefined}
          className={cn(
            "w-full",
            size === "sm" && "h-9 py-1",
            errorMsg && "border-destructive focus:ring-destructive"
          )}
        >
          <SelectValue placeholder={sportsFreez ? "All Sports" : "Select"} />
        </SelectTrigger>
        <SelectContent>
          {/* Default option if not hidden */}
          {!noSelectOpt && (
            <SelectItem value="__SELECT_PLACEHOLDER__">
              {sportsFreez ? "All Sports" : "Select"}
            </SelectItem>
          )}

          {/* "All" option for sport dropdown */}
          {name === "sport" && (
            <SelectItem value=" ">All</SelectItem>
          )}

          {/* Regular options */}
          {options?.map((option) => {
            // Skip empty string values as Radix UI doesn't allow them
            if (option.value === "" || option.value === null || option.value === undefined) {
              return null
            }
            return (
              <SelectItem 
                key={option.id || option.value} 
                value={String(option.value)}
              >
                {option.value === value ? truncateLabel(option.label) : option.label}
              </SelectItem>
            )
          })}
        </SelectContent>
      </ShadcnSelect>

      {errorMsg && (
        <p className="text-sm text-destructive">{errorMsg}</p>
      )}
    </div>
  )
}

export default SelectDropdown
